import { Box, Typography, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { brand } from '../../theme/tokens';

interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Optional call-to-action button(s). */
  action?: ReactNode;
  /** Smaller variant for use inside cards. */
  dense?: boolean;
}

/**
 * Friendly empty / zero-data placeholder with an icon, a reassuring message
 * and an optional next-step action. Replaces blank tables and lists.
 */
const EmptyState = ({ icon, title, description, action, dense }: EmptyStateProps) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    spacing={1.5}
    sx={{ textAlign: 'center', py: dense ? 4 : { xs: 6, sm: 8 }, px: 3 }}
  >
    {icon && (
      <Box
        sx={{
          width: dense ? 60 : 76,
          height: dense ? 60 : 76,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: 'primary.dark',
          background: brand.goldSoft,
          '& svg': { fontSize: dense ? 30 : 38 },
        }}
      >
        {icon}
      </Box>
    )}
    <Typography variant="h6" sx={{ mt: 0.5 }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
        {description}
      </Typography>
    )}
    {action && <Box sx={{ mt: 1 }}>{action}</Box>}
  </Stack>
);

export default EmptyState;
